import { Chip } from "ui/chip/chip";
import { Tooltip } from "ui/tooltip/tooltip";

import { MAX_CHIP_LABEL_LENGTH } from "ui/select/select.constants";
import { getOptionLabelString } from "ui/select/select.helpers";
import { CHIP_TOOLTIP_MAX_WIDTH } from "ui/select/select.styles";

import type { AutocompleteRenderValue, AutocompleteRenderValueGetItemProps } from "@mui/material";
import type { OptionType, SelectSize } from "ui/select/select.types";

type SelectChipsProps<Option extends OptionType> = {
  selected: AutocompleteRenderValue<Option, boolean, false>;
  getItemProps: AutocompleteRenderValueGetItemProps<boolean>;
  size: SelectSize;
  disabled: boolean;
  /** An open list sits over the tags, so their tooltips stay quiet while it is up. */
  isMenuOpen: boolean;
};

/**
 * A multi-select's value: one outlined tag per option, at the field's size. A label past
 * `MAX_CHIP_LABEL_LENGTH` is cut with an ellipsis and shown whole in a tooltip.
 *
 * Returns the tags as a list, with no wrapper of its own, so they stay the input's siblings — the
 * control's `gap` and the input's `:first-child` rules in `getControlSx` depend on it.
 */
export const SelectChips = <Option extends OptionType>({
  selected,
  getItemProps,
  size,
  disabled,
  isMenuOpen,
}: SelectChipsProps<Option>) => {
  // `Multiple extends boolean` makes `selected` a distributive union of a single value and an array.
  if (!Array.isArray(selected)) {
    return null;
  }

  return selected.map((option: Option, index: number) => {
    const { key, ...itemProps } = (getItemProps as AutocompleteRenderValueGetItemProps<true>)({
      index,
    });
    const labelText = getOptionLabelString(option);
    const isClipped = labelText.length > MAX_CHIP_LABEL_LENGTH;

    return (
      <Tooltip
        key={key}
        label={labelText}
        placement="bottom"
        offset={16}
        suppressed={!isClipped || isMenuOpen}
        maxWidth={CHIP_TOOLTIP_MAX_WIDTH}>
        {/* Figma draws a disabled select's tags as resting chips with no delete glyph — the
            control's fill carries the state — where MUI hands each tag `disabled` and `onDelete`. */}
        <Chip
          {...itemProps}
          disabled={false}
          onDelete={disabled ? undefined : itemProps.onDelete}
          size={size}
          variant="outlined"
          label={isClipped ? `${labelText.slice(0, MAX_CHIP_LABEL_LENGTH)}...` : labelText}
        />
      </Tooltip>
    );
  });
};
